import React from "react";
import { useParams } from "react-router-dom";
import Loading from "../../../Loading";
import useDBO from "../../../hooks/useDBO";
import Error from "../../../components/Error";
import CustomerLink from "../../../components/CustomerLink";
import VND from "../../../components/VND";
import SLink from "../../../components/SLink";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import EventNoteIcon from "@mui/icons-material/EventNote";
import { usePrimaryColor } from "../../../components/PrimaryColorContext";


export default function PaymentHistory() {
  let params = useParams();
  const payments = useDBO(`/customer/${params.id}/paymenthistory`);

  if (payments.loading) return (
    <div className="py-20 flex justify-center">
      <Loading />
    </div>
  );
  if (payments.error) return <Error />;

  return (
    <div className="payment-history-container mt-6">
      <header className="flex items-center gap-3 mb-2 px-1">
        <h3 className="roster-title mb-0">Payment History for</h3>
        <span className="text-xl font-black text-slate-800 tracking-tight">
          <CustomerLink customer={params.id} />
        </span>
      </header>

      {payments.get && payments.get.length > 0 ? (
        <div className="space-y-4">
          {payments.get.map((payment) => (
            <DrawPayment payment={payment} key={payment._id} />
          ))}
        </div>
      ) : (
        <div className="text-center py-16 bg-white rounded-xl border border-dashed border-slate-200">
          <div className="text-slate-400 font-medium italic">No payment history found.</div>
        </div>
      )}
    </div>
  );
}

function DrawPayment({ payment }) {
  const dateStr = payment.date ? new Date(payment.date).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric"
  }) : "Unknown Date";

  return (
    <article className="payment-history-card">
      <header className="payment-history-header">
        <div className="flex flex-col gap-2">
          <div className="payment-method-label">
            <EventNoteIcon sx={{ fontSize: 18, color: '${primaryColor}' }} />
            <span>Transaction Record</span>
          </div>
          <time className="timeline-date">{dateStr}</time>
        </div>

        <div className="flex flex-col items-end gap-2">
          <div className="payment-total-amount">
            <VND value={payment.amount} />
          </div>
          {payment.destination && (
            <div className="receipt-status-badge">
              <CheckCircleOutlineIcon sx={{ fontSize: 14 }} />
              <span className="uppercase">{payment.destination}</span>
            </div>
          )}
        </div>
      </header>

      <section className="payment-history-body">
        <DrawBills bills={payment.bills} />
      </section>
    </article>
  );
}

function DrawBills({ bills }) {
  if (!bills || bills.length === 0) return null;
  const students = [...new Set(bills.map((b) => b.student))];

  return (
    <div className="flex flex-col gap-4">
      {students.map((studentId) => (
        <div key={studentId} className="bill-breakdown-student-group">
          <div className="mb-2">
            <span className="student-tag-sm shadow-sm">
              <SLink id={studentId} tab="bills" />
            </span>
          </div>
          <div className="space-y-1 px-1">
            {bills
              .filter((b) => b.student === studentId)
              .map((bill) => (
                <div key={bill._id} className="bill-detail-row">
                  <span className="text-slate-600 italic text-sm">
                    {bill.description || "Educational Course Fee"}
                  </span>
                  <span className="font-bold text-slate-800">
                    <VND value={bill.amount} />
                  </span>
                </div>
              ))}
          </div>
        </div>
      ))}
    </div>
  );
}
